/** Listado por código HS: el documento que declara la partida arancelaria.
 *
 *  Es el hermano del packing con OTRO GRANO: una fila por LOTE, no por
 *  (matrícula × artículo). El mismo artículo sale una vez por cada lote picado,
 *  porque el documento declara la fecha de fabricación y la de caducidad de cada
 *  uno.
 *
 *  La tabla se agrupa por (país de origen, código HS) porque así totaliza el
 *  PDF: revisar una lista plana no diría si un código está bien repartido, que
 *  es exactamente lo que una aduana mira.
 *
 *  Trae su propia consulta y sus propias correcciones. Que sea editable no es un
 *  extra: el código HS falta en un tercio de los artículos que pasan por cargas
 *  (medido: 66 % de cobertura en avi, 68 % en tdis) y es el dato que este
 *  documento existe para declarar — sin poder teclearlo, el usuario sólo podría
 *  emitir un listado que sabe incompleto.
 */
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@tanstack/react-query'

import { Loader } from '@/components/common/Loader'
import { documentosApi } from '@/services/api'
import type {
  DocumentoGenerado,
  DocumentoOrigen,
  HsCodeDatos,
  HsCodeLinea,
  PackingListCabeceraEditable,
} from '@/types'
import {
  agruparHsCode,
  aplicarCorreccionHs,
  camposHsCorregidos,
  camposHsPropagables,
  claveHs,
  clavesHsDelArticulo,
  correccionesHsParaEnviar,
  lineasHsFinales,
  totalesHsCode,
  type CampoHs,
  type CorreccionHs,
} from '@/utils/hsCode'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'

import {
  BloqueAvisos,
  Celda,
  CeldaEditable,
  Formulario,
  OrigenSelector,
  PieGenerar,
  Seccion,
  fmtEnt,
  fmtKg,
  mensajeDeError,
} from './piezas'

/** Los datos de envío que este documento imprime. Menos que el packing: aquí no
 *  hay buque ni notify party, y sí el número de documento. */
const CAMPOS_HS_CABECERA = [
  { clave: 'fecha' as const, tipo: 'date' as const },
  { clave: 'facturaComercial' as const },
  { clave: 'paisOrigen' as const },
  { clave: 'paisDestino' as const },
  { clave: 'incoterm' as const },
  { clave: 'destinatarioNombre' as const },
]

interface Props {
  dark: boolean
  empresa: string
  carga: string
  idioma: 'es' | 'en'
  form: PackingListCabeceraEditable
  onCambioForm: (clave: keyof PackingListCabeceraEditable, valor: string) => void
  onGenerado: (pdf: DocumentoGenerado) => void
}

export function PanelHsCode({
  dark, empresa, carga, idioma, form, onCambioForm, onGenerado,
}: Props) {
  const { t } = useTranslation()
  const [origenForzado, setOrigenForzado] = useState<DocumentoOrigen | null>(null)
  const [correcciones, setCorrecciones] = useState<Record<string, CorreccionHs>>({})
  const [errorGen, setErrorGen] = useState<string | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['hs-code-datos', empresa, carga, origenForzado],
    queryFn: () => documentosApi.getHsCodeDatos(empresa, carga, origenForzado ?? undefined),
    staleTime: 2 * 60 * 1000,
    retry: 1,
    // El diálogo de guardado le quita el foco a la ventana; al volver, un refetch
    // reemplazaría las correcciones a medio escribir.
    refetchOnWindowFocus: false,
  })
  const datos: HsCodeDatos | undefined = data?.data

  // Cambiar de fuente trae otros lotes: arrastrar las correcciones pondría
  // valores de una fuente sobre la otra.
  useEffect(() => { setCorrecciones({}) }, [origenForzado])

  const originales = datos?.lineas ?? []
  const finales = useMemo(
    () => lineasHsFinales(originales, correcciones),
    [originales, correcciones],
  )
  const grupos = useMemo(() => agruparHsCode(finales), [finales])
  const totales = useMemo(() => totalesHsCode(finales), [finales])

  /** Editar una celda la FIJA: lo que el usuario escribe se queda.
   *
   *  Los campos del maestro de ARTÍCULO (código HS, descripción, país de origen)
   *  se propagan a todos sus lotes: son del artículo, no del lote, y teclear el
   *  código lote a lote es el mismo trabajo repetido con la posibilidad añadida
   *  de declarar dos códigos distintos para la misma referencia. */
  function corregir(clave: string, campo: CampoHs, valor: string) {
    const linea = originales.find(l => claveHs(l) === clave)
    const propaga = linea && camposHsPropagables().includes(campo)
    const claves = propaga ? clavesHsDelArticulo(originales, linea!.articulo) : [clave]

    setCorrecciones(prev => {
      const siguiente = { ...prev }
      for (const k of claves) {
        const actual = siguiente[k] ?? { valores: {}, fijados: [], excluida: false }
        siguiente[k] = {
          ...actual,
          valores: { ...actual.valores, [campo]: valor },
          fijados: actual.fijados.includes(campo) ? actual.fijados : [...actual.fijados, campo],
        }
      }
      return siguiente
    })
  }

  function alternarFijado(clave: string, campo: CampoHs) {
    setCorrecciones(prev => {
      const actual = prev[clave] ?? { valores: {}, fijados: [], excluida: false }
      return {
        ...prev,
        [clave]: {
          ...actual,
          fijados: actual.fijados.includes(campo)
            ? actual.fijados.filter(c => c !== campo)
            : [...actual.fijados, campo],
        },
      }
    })
  }

  function alternarExcluida(clave: string) {
    setCorrecciones(prev => {
      const actual = prev[clave] ?? { valores: {}, fijados: [], excluida: false }
      return { ...prev, [clave]: { ...actual, excluida: !actual.excluida } }
    })
  }

  const numCorregidos = useMemo(
    () => originales.reduce((n, o) => {
      const c = correcciones[claveHs(o)]
      if (!c || c.excluida) return n
      return n + camposHsCorregidos(o, aplicarCorreccionHs(o, c)).size
    }, 0),
    [originales, correcciones],
  )
  const numExcluidas = originales.filter(o => correcciones[claveHs(o)]?.excluida).length
  const hayCorrecciones = numCorregidos > 0 || numExcluidas > 0

  const generar = useMutation({
    mutationFn: () => documentosApi.generarHsCode({
      empresa, carga, idioma, cabecera: form, origen: datos?.origen,
      lineas: correccionesHsParaEnviar(originales, correcciones),
    }),
    onSuccess: r => { setErrorGen(null); onGenerado(r.data) },
    onError: (e: unknown) => {
      setErrorGen(mensajeDeError(e) ?? t('salesOrders.documentos.errorGenerar'))
    },
  })

  // Los avisos del backend se calcularon sobre el dato crudo: se filtran los de
  // artículos ya tocados. Dejar «falta el código HS» de una referencia cuyo
  // código el usuario acaba de escribir es pedirle que arregle lo ya arreglado.
  const tocados = new Set(
    originales.filter(o => {
      const c = correcciones[claveHs(o)]
      return c && (c.excluida || camposHsCorregidos(o, aplicarCorreccionHs(o, c)).size > 0)
    }).map(o => o.articulo),
  )
  const vigentes = (datos?.avisos ?? []).filter(a => !a.articulo || !tocados.has(a.articulo))

  // El único bloqueante, recalculado en local para que el botón responda al
  // vuelo: sin bultos el documento declararía cero paquetes de algo que viaja.
  // Se mira lo que el usuario PUEDE arreglar —la casilla de bultos— y no las
  // uds/caja, que aquí son una columna de control y no se editan.
  const bloqueantes = hayCorrecciones
    ? finales.filter(l => !l.bultos)
        .map(l => ({ codigo: 'sin_bultos', bloqueante: true, articulo: l.articulo }))
    : vigentes.filter(a => a.bloqueante)
  const informativos = vigentes.filter(a => !a.bloqueante)
  const puedeGenerar = !!datos && finales.length > 0 && bloqueantes.length === 0

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader label={t('salesOrders.documentos.cargandoDatos')} />
      </div>
    )
  }

  if (isError || !datos) {
    return (
      <div className="flex-1 flex items-center justify-center px-8">
        <p className={`text-[12px] text-center ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
          {t('salesOrders.documentos.errorDatos')}
        </p>
      </div>
    )
  }

  if (generar.isPending) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader label={t('salesOrders.documentos.generando')} />
      </div>
    )
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
        <TablaHsCode
          dark={dark}
          grupos={grupos}
          totales={totales}
          originales={originales}
          correcciones={correcciones}
          origen={datos.origen}
          origenAuto={datos.origenAuto}
          onOrigen={setOrigenForzado}
          onCorregir={corregir}
          onFijar={alternarFijado}
          onExcluir={alternarExcluida}
        />
        <BloqueAvisos dark={dark} bloqueantes={bloqueantes} informativos={informativos} />
        <Formulario
          dark={dark}
          form={form}
          campos={CAMPOS_HS_CABECERA}
          onCambio={onCambioForm}
        />
      </div>

      <PieGenerar
        dark={dark}
        errorGen={errorGen}
        bloqueantes={bloqueantes.length}
        listas={t('salesOrders.documentos.hsCode.lotesListos', { count: finales.length })}
        numCorregidos={numCorregidos}
        numExcluidas={numExcluidas}
        onRestablecer={() => setCorrecciones({})}
        puedeGenerar={puedeGenerar}
        generar={t('salesOrders.documentos.hsCode.generar')}
        onGenerar={() => generar.mutate()}
      />
    </>
  )
}

// ── Tabla ────────────────────────────────────────────────────────────────────

function TablaHsCode({
  dark, grupos, totales, originales, correcciones, origen, origenAuto,
  onOrigen, onCorregir, onFijar, onExcluir,
}: {
  dark: boolean
  grupos: ReturnType<typeof agruparHsCode>
  totales: ReturnType<typeof totalesHsCode>
  originales: HsCodeLinea[]
  correcciones: Record<string, CorreccionHs>
  origen: DocumentoOrigen
  origenAuto: DocumentoOrigen
  onOrigen: (o: DocumentoOrigen | null) => void
  onCorregir: (clave: string, campo: CampoHs, valor: string) => void
  onFijar: (clave: string, campo: CampoHs) => void
  onExcluir: (clave: string) => void
}) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()

  const border = dark ? 'border-white/[0.07]' : 'border-slate-200'
  const divider = dark ? 'border-white/[0.05]' : 'border-slate-100'
  const th = `px-2 py-1.5 text-[9px] font-bold uppercase tracking-wider text-left ${
    dark ? 'text-slate-500' : 'text-slate-400'}`
  const td = `px-2 py-1 text-[11px] ${dark ? 'text-slate-300' : 'text-slate-700'}`
  const totalCls = `px-2 py-1.5 text-[11px] font-bold ${dark ? 'text-slate-100' : 'text-slate-900'}`

  // 11 columnas de dato + la de excluir. Son las MISMAS que imprime el PDF y en
  // el mismo orden: la tabla es «lo que va a salir», no un resumen de ella.
  const COLS = 12
  const porClave = new Map(originales.map(l => [claveHs(l), l]))

  if (grupos.length === 0) {
    return (
      <Seccion titulo={t('salesOrders.documentos.hsCode.title')} dark={dark}>
        <p className={`text-[11px] ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
          {t('salesOrders.documentos.hsCode.vacio')}
        </p>
      </Seccion>
    )
  }

  return (
    <Seccion
      titulo={t('salesOrders.documentos.hsCode.title')}
      nota={t('salesOrders.documentos.hsCode.nota')}
      dark={dark}
      accion={
        <OrigenSelector dark={dark} activo={origen} auto={origenAuto} onCambio={onOrigen} />
      }
    >
      <div className={`border rounded-lg overflow-x-auto ${border}`}>
        <table className="w-full border-collapse min-w-[1180px]">
          <thead>
            <tr className={`border-b ${border}`}>
              <th className={th}>{t('salesOrders.documentos.hsCode.articulo')}</th>
              <th className={th}>{t('salesOrders.documentos.hsCode.descripcion')}</th>
              <th className={th}>{t('salesOrders.documentos.hsCode.hsCode')}</th>
              <th className={th}>{t('salesOrders.documentos.hsCode.origen')}</th>
              <th className={th}>{t('salesOrders.documentos.hsCode.lote')}</th>
              <th className={th}>{t('salesOrders.documentos.hsCode.caducidad')}</th>
              <th className={`${th} text-right`}>{t('salesOrders.documentos.hsCode.cantidad')}</th>
              <th className={`${th} text-right`}>{t('salesOrders.documentos.hsCode.bultos')}</th>
              <th className={`${th} text-right`}>{t('salesOrders.documentos.hsCode.precio')}</th>
              <th className={`${th} text-right`}>{t('salesOrders.documentos.hsCode.importe')}</th>
              <th className={`${th} text-right`}>{t('salesOrders.documentos.hsCode.neto')}</th>
              <th className={`${th} text-right`}>{t('salesOrders.documentos.hsCode.bruto')}</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {grupos.map(grupo => (
              <React.Fragment key={grupo.clave}>
                {/* Cabecera del grupo: así totaliza el PDF, por (origen, HS). */}
                <tr className={`border-t ${divider} ${
                  dark ? 'bg-white/[0.04]' : 'bg-slate-100/70'}`}>
                  <td colSpan={COLS} className={`px-2 py-1 text-[10px] ${
                    dark ? 'text-slate-200' : 'text-slate-700'}`}>
                    <span className={`uppercase tracking-wider font-bold ${
                      dark ? 'text-slate-500' : 'text-slate-400'}`}>
                      {t('salesOrders.documentos.hsCode.hsCode')}
                    </span>
                    <span className={`ml-2 font-mono font-semibold ${
                      grupo.hscode ? '' : (dark ? 'text-amber-400/80' : 'text-amber-600')}`}>
                      {grupo.hscode || t('salesOrders.documentos.hsCode.sinCodigo')}
                    </span>
                    <span className="ml-3 opacity-80">
                      {t('salesOrders.documentos.hsCode.origen')}{' '}
                      <span className="font-semibold">{grupo.origen || '—'}</span>
                    </span>
                    <span className="ml-3 opacity-80">
                      {t('salesOrders.documentos.hsCode.bultos')}{' '}
                      <span className="font-semibold tabular-nums">{fmtEnt(grupo.totales.bultos)}</span>
                    </span>
                    <span className="ml-3 opacity-80">
                      {t('salesOrders.documentos.hsCode.neto')}{' '}
                      <span className="font-semibold tabular-nums">{fmtKg(grupo.totales.neto)}</span>
                    </span>
                  </td>
                </tr>

                {grupo.lineas.map(l => {
                  const clave = claveHs(l)
                  const original = porClave.get(clave) ?? l
                  const c = correcciones[clave]
                  const corregidos = camposHsCorregidos(original, l)
                  const excluida = !!c?.excluida
                  return (
                    <FilaHs
                      key={clave}
                      dark={dark} td={td} divider={divider} fmtArt={fmtArt}
                      linea={l} original={original} clave={clave}
                      corregidos={corregidos}
                      fijados={new Set(c?.fijados ?? [])}
                      excluida={excluida}
                      onCorregir={onCorregir}
                      onFijar={onFijar}
                      onExcluir={onExcluir}
                    />
                  )
                })}
              </React.Fragment>
            ))}

            <tr className={`border-t-2 ${border} ${dark ? 'bg-white/[0.03]' : 'bg-slate-50'}`}>
              <td className={totalCls} colSpan={6}>
                {t('salesOrders.documentos.contenido.totales')}
              </td>
              <td className={`${totalCls} text-right tabular-nums`}>{fmtEnt(totales.cantidad)}</td>
              <td className={`${totalCls} text-right tabular-nums`}>{fmtEnt(totales.bultos)}</td>
              {/* El precio unitario no se totaliza: sumar precios no significa
                  nada. El PDF deja esa casilla con la suma de los unitarios;
                  aquí se deja vacía a propósito. */}
              <td className={totalCls} />
              <td className={`${totalCls} text-right tabular-nums`}>{fmtKg(totales.importe)}</td>
              <td className={`${totalCls} text-right tabular-nums`}>{fmtKg(totales.neto)}</td>
              <td className={`${totalCls} text-right tabular-nums`}>{fmtKg(totales.bruto)}</td>
              <td className={totalCls} />
            </tr>
          </tbody>
        </table>
      </div>
    </Seccion>
  )
}

function FilaHs({
  dark, td, divider, fmtArt, linea: l, original, clave, corregidos, fijados,
  excluida, onCorregir, onFijar, onExcluir,
}: {
  dark: boolean
  td: string
  divider: string
  fmtArt: (a: string) => string
  linea: HsCodeLinea
  original: HsCodeLinea
  clave: string
  corregidos: Set<CampoHs>
  fijados: Set<CampoHs>
  excluida: boolean
  onCorregir: (clave: string, campo: CampoHs, valor: string) => void
  onFijar: (clave: string, campo: CampoHs) => void
  onExcluir: (clave: string) => void
}) {
  const { t } = useTranslation()

  const celda = (
    campo: CampoHs,
    texto: string | null,
    opciones?: { derivado?: boolean; plano?: boolean },
  ) => (
    <CeldaEditable
      dark={dark} td={td} campo={campo}
      valor={l[campo] as string | number | null | undefined}
      original={original[campo] as string | number | null | undefined}
      texto={texto}
      corregido={corregidos.has(campo)}
      fijado={fijados.has(campo)}
      derivado={!!opciones?.derivado}
      bloqueado={excluida}
      plano={opciones?.plano}
      onCambio={v => onCorregir(clave, campo, v)}
      onFijar={() => onFijar(clave, campo)}
    />
  )

  return (
    <tr className={`border-t ${divider} ${excluida ? 'opacity-40 line-through' : ''}`}>
      {/* La referencia NO se edita: identifica la línea. Cambiarla no corregiría
          un dato, sería declarar otro artículo. */}
      <td className={`${td} font-mono text-[10px] whitespace-nowrap`}>{fmtArt(l.articulo)}</td>
      {celda('descripcion', l.descripcion || null)}
      {celda('hscode', l.hscode || null)}
      {celda('origen', l.origen || null)}
      <Celda td={td} dark={dark} valor={l.lote ?? null} />
      <Celda td={td} dark={dark} valor={l.fechaCaducidad ?? null} />
      {celda('cantidad', fmtEnt(l.cantidad))}
      {celda('bultos', fmtEnt(l.bultos))}
      {celda('precioUnitario', fmtKg(l.precioUnitario))}
      {celda('precioNeto', fmtKg(l.precioNeto), { derivado: true })}
      {celda('pesoNeto', fmtKg(l.pesoNeto))}
      {celda('pesoBruto', fmtKg(l.pesoBruto))}
      <td className={`${td} text-right`}>
        <button
          type="button"
          onClick={() => onExcluir(clave)}
          title={t(excluida
            ? 'salesOrders.documentos.correcciones.incluirTooltip'
            : 'salesOrders.documentos.correcciones.excluirTooltip')}
          className={`p-0.5 rounded cursor-pointer transition-colors ${
            excluida
              ? (dark ? 'text-sky-300' : 'text-sky-600')
              : (dark ? 'text-slate-700 hover:text-red-400' : 'text-slate-300 hover:text-red-500')}`}
        >
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2" strokeLinecap="round">
            {excluida ? <path d="M12 5v14M5 12h14" /> : <path d="M18 6L6 18M6 6l12 12" />}
          </svg>
        </button>
      </td>
    </tr>
  )
}
